import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { InactiveRoom } from '../Commons/InactiveRoom';
import { DynamicsRegistry } from './DynamicsRegistry';
import { Loading } from '../Commons/Loading';
import SignalRService from '../../utilities/WS/SignalRService';
import { DeleteSessionData } from '../../utilities/BL/Sessions';

export function RoomLoader({ children }) {
  const [searchParams] = useSearchParams();
  const roomId = searchParams.get('roomId');
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [roomData, setRoomData] = useState(null);
  const [defaultView, setDefaultView] = useState('start');
  const lastTransitionRef = useRef({ targetDynamic: '', timestamp: 0 });
  const roomDataRef = useRef(roomData);

  useEffect(() => {
    roomDataRef.current = roomData;
  }, [roomData]);

  const API_URL = process.env.REACT_APP_API_URL || 'https://localhost:50586/api/';

  useEffect(() => {
    const bootstrap = async () => {
      setLoading(true);
      setError(null);
      
      try {
        let endpoint = `${API_URL}v2/rooms/bootstrap/default`;
        if (roomId) {
          endpoint = `${API_URL}v2/rooms/${roomId}/bootstrap`;
        }

        const res = await fetch(endpoint);
        
        if (res.status === 404) {
          // If we are looking for default/global room and it's not found, we fallback to V1 children
          if (!roomId) {
            setRoomData(null);
            setLoading(false);
            return;
          } else {
            setError('La sala especificada no fue encontrada o está inactiva.');
            setLoading(false);
            return;
          }
        }

        if (!res.ok) {
          setError('Error al conectar con la sala.');
          setLoading(false);
          return;
        }

        const data = await res.json();
        
        // If the event status is not Active, we treat it as inactive
        if (data.status !== 'Active') {
          setError('La sala está inactiva porque el evento asociado no está activo.');
          setLoading(false);
          return;
        }

        setRoomData(data);
      } catch (err) {
        //console.error('Error bootstrapping room:', err);
        // On connection errors, if we don't have an explicit roomId, we can fallback to V1 children to prevent lockouts
        if (!roomId) {
          setRoomData(null);
        } else {
          setError('Error de conexión con el servidor.');
        }
      } finally {
        setLoading(false);
      }
    };

    bootstrap();
  }, [roomId, API_URL]);

  const [transitionPayload, setTransitionPayload] = useState(null);

  useEffect(() => {
    const activeRoomId = roomId || roomData?.roomId;
    if (activeRoomId) {
      const originalFetch = window.fetch;
      window.fetch = function (url, options = {}) {
        options.headers = options.headers || {};
        if (options.headers instanceof Headers) {
          options.headers.set('X-Room-Id', activeRoomId);
          options.headers.set('roomId', activeRoomId);
        } else {
          options.headers['X-Room-Id'] = activeRoomId;
          options.headers['roomId'] = activeRoomId;
        }
        return originalFetch(url, options);
      };
      return () => {
        window.fetch = originalFetch;
      };
    }
  }, [roomId, roomData?.roomId]);

  useEffect(() => {
    const activeRoomId = roomId || roomData?.roomId;

    const handleSaaSTransition = (message) => {
      if (!message || !message.type) return;

      // Aislamiento de roomId
      const msgRoomId = message.roomId || message.RoomId;
      if (activeRoomId) {
        if (!msgRoomId || String(msgRoomId).toLowerCase() !== String(activeRoomId).toLowerCase()) {
          return;
        }
      } else {
        if (msgRoomId) {
          return;
        }
      }

      const type = message.type.toLowerCase();
      if (type === 'force_relogin') {
        const isScreenPath = window.location.pathname.toLowerCase().includes('/screen');
        if (isScreenPath) {
          console.log("[RoomLoader] Ignorando force_relogin en la pantalla gigante.");
          return;
        }
        DeleteSessionData().then(() => {
          window.location.reload(true);
        });
        return;
      }
      let targetDynamic = undefined; // undefined = no aplica, null = volver al default (welcome/wait), string = dinámica específica

      if (type === 'set.dynamic') {
        const dyn = message.data?.dynamic?.toLowerCase();
        if (dyn === 'welcome' || dyn === 'wait' || dyn === 'thanks' || dyn === 'start') {
          targetDynamic = null;
          setDefaultView(dyn === 'start' ? 'welcome' : dyn);
        } else {
          targetDynamic = message.data?.dynamic;
        }
      } else if (type === 'newquestionquizzwithimageandoptions' || type === 'trivia.question' || type === 'trivia.active') {
        targetDynamic = 'trivia';
      } else if (type === 'quizzwithimageandoptions' || type === 'trivia.start' || type === 'trivia.layout') {
        targetDynamic = null;
      } else if (type.startsWith('roulette.')) {
        if (type === 'roulette.layout' || type === 'roulette.prepare') {
          targetDynamic = null;
        } else {
          targetDynamic = 'roulette';
        }
      } else if (type.startsWith('slotmachine.')) {
        if (type === 'slotmachine.layout' || type === 'slotmachine.prepare') {
          targetDynamic = null;
        } else {
          targetDynamic = 'slot';
        }
      } else if (type.startsWith('balloons.')) {
        targetDynamic = 'balloons';
      } else if (type === 'riddle' || type === 'newquestionriddler' || type === 'resumequestionriddler' || type === 'riddledisplayitem') {
        targetDynamic = 'riddle';
      } else if (type.startsWith('luchofest.') || type === 'frontpageluchofest') {
        if (type === 'frontpageluchofest') {
          targetDynamic = null;
        } else {
          targetDynamic = 'luchofest';
        }
      } else if (type.startsWith('goal.') || type.startsWith('runtogoal.')) {
        targetDynamic = 'goal';
      } else if (type.startsWith('picnic.')) {
        targetDynamic = 'picnic';
      } else if (type.startsWith('taco.')) {
        targetDynamic = 'tacos';
      } else if (type.startsWith('noisemeter.')) {
        targetDynamic = 'noisemeter';
      } else if (type.startsWith('voting.')) {
        targetDynamic = 'voting';
      } else if (type.startsWith('caps.')) {
        targetDynamic = 'caps';
      } else if (type.startsWith('briefcase.')) {
        if (type === 'briefcase.layout') {
          targetDynamic = null;
        } else {
          targetDynamic = 'briefcase';
        }
      } else if (type === 'catchtheball') {
        targetDynamic = 'catchtheball';
      } else if (type.startsWith('colors.')) {
        targetDynamic = 'colors';
      } else if (type.startsWith('sequence.')) {
        targetDynamic = 'sequence';
      } else if (type.startsWith('lottery.')) {
        targetDynamic = 'lottery';
      } else if (type.startsWith('lightshow.') || type === 'showlightshowqr') {
        targetDynamic = 'lightshow';
      } else if (type === 'welcome' || type === 'wait' || type === 'thanks' || type === 'start' || type === 'qrmessages' || type.startsWith('qrmessages.')) {
        targetDynamic = null;
        if (type === 'welcome' || type === 'wait' || type === 'thanks' || type === 'start') {
          setDefaultView(type === 'start' ? 'welcome' : type);
        }
      }

      // Protección contra indefinidos: Si el targetDynamic es undefined, no intentamos transicionar.
      // Simplemente ignoramos la transición y propagamos el payload.
      if (targetDynamic === undefined) {
        //console.log(`[RoomLoader] Ignorando transición para evento de control/sistema: ${type}`);
        setTransitionPayload(message);
        return;
      }

      const currentDynamic = roomDataRef.current?.nombreIdentificador?.toLowerCase() || null;
      const normalizedTarget = targetDynamic ? targetDynamic.toLowerCase() : null;

      // Si el mensaje es un evento de la dinámica activa (ej. lightshow.start)
      if (currentDynamic === normalizedTarget) {
        //console.log(`[RoomLoader] Evento de dinámica activa detectado (${type}). Ignorando transición y propagando payload.`);
        setTransitionPayload(message);
        return;
      }

      // Si la dinámica objetivo es diferente, la transición debe ocurrir inmediatamente
      //console.log(`[RoomLoader] Transición de dinámica detectada de ${currentDynamic} hacia: ${normalizedTarget}`);

      const now = Date.now();
      lastTransitionRef.current = { targetDynamic: normalizedTarget, timestamp: now };

      setTransitionPayload(message);
      setRoomData(prev => {
        if (prev && prev.nombreIdentificador?.toLowerCase() === normalizedTarget) {
          return prev;
        }
        return prev 
          ? { ...prev, nombreIdentificador: targetDynamic } 
          : { roomId: activeRoomId, nombreIdentificador: targetDynamic, status: 'Active' };
      });
    };

    SignalRService.on('message', handleSaaSTransition);
    return () => {
      SignalRService.off('message', handleSaaSTransition);
    };
  }, [roomId, roomData?.roomId]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <InactiveRoom message={error} />;
  }

  // Detectar rol de forma robusta
  const pathname = window.location.pathname.toLowerCase();
  let isScreen = pathname.includes('/screen');
  
  React.Children.forEach(children, child => {
    if (child && child.type && (child.type.name === 'Screen' || child.type.name === 'ScreenV2')) {
      isScreen = true;
    }
  });

  // Las páginas auxiliares como /selfiefan, /messages, /playlist y /sequence se quedan disponibles siempre, sin importar la dinámica activa
  const isAuxiliaryPage = pathname.includes('/selfiefan') || pathname.includes('/messages') || pathname.includes('/playlist') || pathname.includes('/sequence');



  // Fallback to V1 dashboard or screen, passing the resolved roomId and defaultView as props
  return React.Children.map(children, child => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { 
        roomId: roomData?.roomId,
        defaultView: defaultView
      });
    }
    return child;
  });
}
export default RoomLoader;
